"use client";

import React, { useEffect } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { QueryKeys } from "@cede/utils";
import { CashbackHistory } from "./CashbackHistory";
import { PortalModal } from "@/components/ui/portal-modal";
import { useDevice, useHubUser } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useQueryClient } from "@tanstack/react-query";

interface CashbackDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Cashback dialog component for viewing and claiming cashback rewards
 */
export const CashbackDialog: React.FC<CashbackDialogProps> = ({ open, onClose }) => {
  const { isMobile } = useDevice();
  const queryClient = useQueryClient();
  const { hubUser } = useHubUser();
  const { trackEvent } = useMetricsEvents();

  // Track dialog open and refresh data
  useEffect(() => {
    if (open) {
      trackEvent(METRICS_EVENTS.CASHBACK_DIALOG_OPENED, {
        hubUserId: hubUser?.id,
      });
      // Refresh cashback data when dialog opens
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CASHBACK_STATUS] });
    }
  }, [open, hubUser?.id, trackEvent, queryClient]);

  const handleDialogClose = () => {
    trackEvent(METRICS_EVENTS.CASHBACK_DIALOG_CLOSED, {
      hubUserId: hubUser?.id,
    });
    onClose();
  };

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        handleDialogClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open]);

  return (
    <PortalModal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleDialogClose();
      }}
      showCloseButton
      closeButtonClassName={isMobile ? "top-4 right-4" : "top-6 right-6"}
    >
      <PortalModal.Header>
        <PortalModal.Title className="text-2xl font-bold text-cede-primary px-0 text-center">
          Cashback Rewards
        </PortalModal.Title>
      </PortalModal.Header>

      <PortalModal.Content className="relative flex-1 min-h-0 flex flex-col pt-4" scrollable>
        <CashbackHistory />
      </PortalModal.Content>
    </PortalModal>
  );
};
